import { useState } from "react"
import AsciiAssembly from "@/components/AsciiAssembly/AsciiAssembly"
import Badge from "@/components/Badge/Badge"
import GeneratingScreen, { GENERATING_MS } from "@/components/GeneratingScreen/GeneratingScreen"
import GridBackdrop from "@/components/GridBackdrop/GridBackdrop"
import Spinner from "@/components/Spinner/Spinner"
import { GATE_WAIT, OVERLAY_WAIT, PICK_WAIT, overlayCopy } from "@/content/overlays"
import { ASCII } from "@/content/ascii"
import { PAINS_COPY } from "@/content/pains"
import { completeJob, startJob } from "@/services/jobs"
import { advanceFromOverlay, setOverlaySelection } from "@/services/sessions"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { useSessionStore } from "@/store/sessionStore"
import {
  painsByVotes,
  peopleFor,
  selectJobs,
  selectOverlay,
  selectPains,
  selectSession,
} from "@/store/selectors"
import { useCurtainMotion } from "./useCurtainMotion"
import type { Phase, Round } from "@/types"
import styles from "@/styles/components/RoundTransition.module.css"

interface RoundTransitionProps {
  canDrive: boolean
}

const TITLE_ID = "round-transition-title"
const BODY_ID = "round-transition-body"
const NO_PAINS_LEFT = "Every voted pain point has been worked. Time for the report."
/*
 * The pick screen with nothing to pick. It should not be reachable —
 * `RoundControls` only raises `jobPick` when a pain has a vote — but a curtain
 * whose only button is disabled is a room that cannot be moved on, so the CTA
 * stays live and this says what pressing it will do.
 */
const NO_PAINS_TO_PICK =
  "No voted pain point is left to take on. Continue and write the problem yourself."

/*
 * The writes a button here waits on. `ADVANCE` covers the CTA on both curtains;
 * the gate's "finish" path replaces the whole screen with the generating
 * animation instead, so its spinner is never actually seen. `OPEN_JOB` is the
 * one write that both pain screens make — the gate's "explore this problem" and
 * the pick screen's only button are the same action on the same list.
 */
const ADVANCE = "advance"
const OPEN_JOB = "openJob"

/** Stable empties, so the idle hook call does not allocate on every render. */
const NO_MOTIF: readonly string[] = []
const NO_LABELS: readonly string[] = []

/**
 * Full-screen transition between rounds, driven by shared session state.
 * Renders nothing when there is no overlay.
 *
 * The facilitator advances the room in one atomic write.
 */
export default function RoundTransition({ canDrive }: RoundTransitionProps) {
  const session = useSessionStore(selectSession)
  const overlay = useSessionStore(selectOverlay)
  const pains = useSessionStore(selectPains)
  const jobs = useSessionStore(selectJobs)
  const participants = useSessionStore((s) => s.participants)
  const { write, isPending } = useFirestoreWrite()
  const [generating, setGenerating] = useState(false)

  /*
   * The curtain's motion is a hook, so it has to run whether or not there is a
   * curtain — React does not allow the "nothing to show" case to skip it. It is
   * cheap when idle: with no overlay there is no motif, and `useCurtainMotion`
   * reads that as "nothing to assemble" and starts no animation frame at all.
   *
   * The key is the overlay's kind, or the absence of one. Keying on kind alone
   * would leave a curtain raised twice for the same round already assembled the
   * second time, because this component stays mounted for the whole session —
   * the null in between is what makes it replay.
   */
  const kind = overlay?.kind ?? null
  const copy = kind ? overlayCopy(kind, session?.config ?? null) : null
  const motion = useCurtainMotion({
    lines: copy ? ASCII[copy.motif].lines : NO_MOTIF,
    labels: copy?.steps?.map((step) => step.label) ?? NO_LABELS,
    decision: kind === "jobGate" || kind === "jobPick",
    replayKey: kind ?? "none",
  })

  if (!session || !overlay || !copy) return null

  /*
   * The two overlays that ask which problem becomes a job: the gate after a job
   * is finished, and the pick screen after the vote. Same list, same write —
   * they differ only in where the action sits. On the gate it is the alternative
   * to finishing; on the pick screen it is the only way out.
   */
  const isGate = kind === "jobGate"
  const isPick = kind === "jobPick"
  const choosesPain = isGate || isPick
  const { destination } = copy

  // The label promises a destination, so the write has to deliver one. Never a
  // bare dismissal: that would leave the room in the round it was already in
  // while the button said otherwise.
  const advance = () =>
    write(() => advanceFromOverlay(session.id, destination.round, destination.phase), ADVANCE)

  /*
   * Finishing closes the job first. Without that, `currentJobId` still points at
   * a job with no `completedAt`, and the wrap screen would read it as work in
   * progress.
   *
   * The latch is dropped in a `finally`, not left set: this component stays
   * mounted for the rest of the session, so a latch that survives its own write
   * renders the generating curtain over the NEXT overlay — a gate with no CTA
   * that only a reload escapes. A rejected `completeJob` has to clear it too, or
   * the facilitator is left watching a report that is not being made.
   */
  const finish = () => {
    setGenerating(true)
    write(async () => {
      try {
        if (session.currentJobId) await completeJob(session.id, session.currentJobId)
        await new Promise((resolve) => window.setTimeout(resolve, GENERATING_MS))
        await advanceFromOverlay(session.id, destination.round, destination.phase)
      } finally {
        setGenerating(false)
      }
    }, ADVANCE)
  }

  const selection = overlay.selection
  /*
   * Every pain that won a vote and has not been worked yet. Excluded by job
   * rather than by a flag on the pain: a job records which pains it came from,
   * so the list is derived and a job created twice cannot desynchronise it.
   *
   * The pain currently HIGHLIGHTED stays on the list even once a job has it,
   * because that is the row the room is looking at. Opening a job is two
   * transactions, and a transaction has no local application: `startJob`'s
   * document comes back from the server a whole round trip before
   * `advanceFromOverlay` clears the curtain. Without the exemption the ticked
   * row drops out the instant the job lands and the list reflows on every
   * screen in the room — a different list, for one beat, immediately before the
   * round changes, which reads as a misclick rather than as progress.
   *
   * It cannot leak into the next gate: `showOverlay` raises every curtain with
   * `selection: null`, so a worked pain is off the list and can never be
   * highlighted again.
   */
  const worked = new Set(jobs.flatMap((job) => job.painIds))
  const available = painsByVotes(pains).filter(
    (pain) => pain.voterIds.length > 0 && (!worked.has(pain.id) || pain.id === selection)
  )
  /*
   * Whether there is another problem to take on.
   *
   * With the Pains round on, that means one of its pains: a job with no pain
   * behind it is exactly what the round exists to prevent, so an exhausted board
   * leaves only the write-up. With it off there is no list to pick from and the
   * facilitator writes the problem themselves, so it is always available.
   */
  const painsOn = session.config.painsEnabled !== false
  const canTakeAnother = painsOn ? selection !== null : true
  /*
   * The pick screen has no second answer, so its button may not be dead: with a
   * pain chosen it opens a job for it, and with an empty list — which should not
   * happen, see NO_PAINS_TO_PICK — it opens one the facilitator writes instead.
   */
  const canPick = selection !== null || available.length === 0

  const finishing = isPending(ADVANCE)
  const openingJob = isPending(OPEN_JOB)

  /*
   * Opening a job for the chosen pain and moving the room are two writes, not
   * one, because `startJob` is itself a transaction that repoints
   * `session.currentJobId`. `startJob` is idempotent per pain set, so a double
   * press cannot fork the session into two jobs for the same problem.
   */
  const openJob = (dest: { round: Round; phase: Phase }) => {
    /*
     * Seeded the same way whichever screen asked: with the user story off there
     * is no drafting exercise, so the field arrives holding the pain the room
     * voted for and the round becomes a read-back. Without this the problem
     * lands on an empty field the facilitator has to retype from the screen they
     * just left.
     */
    const chosen = selection ? pains.find((pain) => pain.id === selection) : undefined
    const seeded = chosen && !session.config.userStoryEnabled ? { what: chosen.text } : undefined
    write(async () => {
      await startJob(session.id, selection ? [selection] : [], seeded)
      await advanceFromOverlay(session.id, dest.round, dest.phase)
    }, OPEN_JOB)
  }

  const takeAnother = () => {
    if (!copy.altDestination) return
    openJob(copy.altDestination)
  }

  /*
   * Only the facilitator sees this: they are the one who pressed the button, and
   * the room's own curtain drops when the round actually changes. Showing it to
   * everyone would put a spinner on five screens for a write that already landed.
   */
  if (generating) {
    /*
     * What the room actually produced, counted the same way the report is:
     * derived from the job documents, never stored. `cardsPlaced` walks every
     * job's `placements` rather than its `cardIds` — a shortlisted card the
     * facilitator never filed is not on the board.
     */
    return (
      <GeneratingScreen
        tally={{
          jobs: jobs.length,
          painsVoted: pains.filter((pain) => pain.voterIds.length > 0).length,
          cardsPlaced: jobs.reduce((sum, job) => sum + Object.keys(job.placements).length, 0),
        }}
      />
    )
  }

  return (
    <div
      className={styles.curtain}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      aria-describedby={BODY_ID}
    >
      <GridBackdrop />

      <div className={styles.inner}>
        <AsciiAssembly
          rows={motion.motif}
          columns={motion.motifColumns}
          cell={motion.motifCell}
          fontSize={motion.motifFontSize}
          lineHeight={motion.motifLineHeight}
          className={styles.motif}
        />

        <span className={styles.chip} style={motion.badgeStyle} aria-hidden="true">
          <span className={styles.chipNum}>{copy.num}</span>
          {copy.badge}
        </span>

        <h1 className={styles.title} id={TITLE_ID} style={motion.titleStyle}>
          {copy.title}
        </h1>
        <p className={styles.body} id={BODY_ID} style={motion.bodyStyle}>
          {copy.body}
        </p>

        {copy.steps ? (
          <div className={styles.stepsFrame}>
            {/* The scan line, crossing the three beats once as they land. A
                sibling of the list rather than a child of it: it is a lighting
                effect on the row, not an item in it. */}
            <div className={styles.sweep} style={motion.sweepStyle} aria-hidden="true" />
            <ol className={styles.steps}>
              {copy.steps.map((step, index) => {
                const beat = motion.steps[index]
                return (
                  <li className={styles.step} key={step.label}>
                    <span
                      className={beat.settled ? styles.stepNumber : styles.stepNumberIdle}
                      style={beat.numStyle}
                      aria-hidden="true"
                    >
                      {beat.num}
                    </span>
                    {/* The decoded text is noise for most of its life, so the
                      readable label is what is announced and the scramble is
                      hidden. */}
                    <span
                      className={beat.labelSettled ? styles.stepLabel : styles.stepLabelScan}
                      style={beat.labelStyle}
                    >
                      <span aria-hidden="true">{beat.label}</span>
                      <span className={styles.srOnly}>{step.label}</span>
                    </span>
                    <span className={styles.stepDetail} style={beat.detailStyle}>
                      {step.detail}
                    </span>
                  </li>
                )
              })}
            </ol>
          </div>
        ) : null}

        {/*
         * The pain list is shown to everyone, not just the facilitator: the room
         * voted for these, and watching one get picked is the point — even more
         * so on the pick screen, where the choice follows a vote everyone just
         * cast. Only the facilitator's clicks write.
         */}
        {choosesPain && available.length > 0 ? (
          <ul className={styles.pains} style={motion.listStyle}>
            {available.map((pain) => {
              const chosen = selection === pain.id
              const [author] = pain.authorId ? peopleFor(participants, [pain.authorId]) : []
              return (
                <li key={pain.id}>
                  <button
                    type="button"
                    className={chosen ? `${styles.pain} ${styles.painOn}` : styles.pain}
                    aria-pressed={chosen}
                    /*
                     * Locked once a job is being opened: the job is created for
                     * the selection as it was at the press, so a tick that moved
                     * afterwards would name a problem the room is not about to
                     * explore.
                     */
                    disabled={!canDrive || openingJob}
                    aria-busy={isPending(`pain-${pain.id}`) || undefined}
                    onClick={() =>
                      write(() => setOverlaySelection(session.id, pain.id), `pain-${pain.id}`)
                    }
                  >
                    <span className={styles.radio} aria-hidden="true">
                      {isPending(`pain-${pain.id}`) ? <Spinner size="sm" /> : chosen ? "✓" : null}
                    </span>
                    {author ? <Badge name={author.name} size={28} family={author.family} /> : null}
                    <span className={styles.painText}>“{pain.text}”</span>
                    <span className={styles.painVotes}>
                      {PAINS_COPY.voteCount(pain.voterIds.length)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}

        {canDrive && painsOn && available.length === 0 && choosesPain ? (
          <p
            className={motion.noteSettled ? styles.exhausted : styles.exhaustedScan}
            style={motion.listStyle}
          >
            <span aria-hidden="true">{motion.note(isGate ? NO_PAINS_LEFT : NO_PAINS_TO_PICK)}</span>
            <span className={styles.srOnly}>{isGate ? NO_PAINS_LEFT : NO_PAINS_TO_PICK}</span>
          </p>
        ) : null}

        {canDrive ? (
          <div className={styles.actions} style={motion.actionsStyle}>
            {/*
             * On the gate the two answers swap weight: taking another problem is
             * the decision the screen is asking about, so it carries the fill and
             * finishing sits beside it. Neither is pressable while the other's
             * write is out — they go to different rounds.
             */}
            {isGate && copy.altCta ? (
              <button
                type="button"
                className={styles.cta}
                disabled={!canTakeAnother || openingJob || finishing}
                aria-busy={openingJob || undefined}
                onClick={takeAnother}
              >
                {openingJob ? <Spinner size="sm" /> : null}
                {copy.altCta}
              </button>
            ) : null}
            {/*
             * One button, three meanings: the gate's quiet "finish", the pick
             * screen's "explore this problem" — which opens a job for the pain
             * the list chose — and every other curtain's plain advance.
             */}
            <button
              type="button"
              className={isGate ? styles.alt : styles.cta}
              disabled={isPick ? !canPick || openingJob : finishing || openingJob}
              aria-busy={(isPick ? openingJob : finishing) || undefined}
              onClick={isGate ? finish : isPick ? () => openJob(destination) : advance}
            >
              {(isPick ? openingJob : finishing) ? <Spinner size="lg" /> : null}
              {copy.cta}
            </button>
          </div>
        ) : (
          <p className={styles.wait} style={motion.actionsStyle}>
            <span className={styles.dot} aria-hidden="true" />
            {isGate ? GATE_WAIT : isPick ? PICK_WAIT : OVERLAY_WAIT}
          </p>
        )}
      </div>
    </div>
  )
}
